import * as React from 'react';
import { mxStrings } from 'utils/JobCollabStrings';
import { buildFullName,
    extractInitials } from 'utils/jobCollabEmployeeUtils';
import CircleDisplay from 'nomst/Components/SharedJobCollab/circleDisplay';
import { buildClassString } from '@payscale/payscale-shared-ui/lib/StringUtils';
import { CompensationCard } from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/CompensationCard';
import { EmployeeDetailsCard } from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/EmployeeDetailsCard';
import { PerformanceRatingCard } from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/PerformanceRatingCard';
import { RangeDistributionCard } from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/RangeDistributionCard';
import { RangePenetrationCard } from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/RangePenetrationCard';
import { TotalCashCompensationCard } from '@payscale/payscale-shared-ui/lib/EmployeeDetailsCards/TotalCashCompensationCard';
import DetailsSection from 'nomst/Components/SharedJobCollab/detailsSection';
import { formatDate } from 'utils/dateUtils';
import { Employee } from 'nomst/Stores/Decision/employee';
import ContentContainer from 'nomst/Components/SharedJobCollab/contentContainer';
import { employeeList, navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { observer } from 'mobx-react';
import './employeeDetailsView.scss';

const EmployeeDetailsView = (): React.ReactElement => {
    const { selectedEmployee } = employeeList;
    const noDetails = employeeList.employeeDetailsFetched
        && (!selectedEmployee || Object.keys(selectedEmployee).length === 0);

    const renderNoDetailsMessage = (): React.ReactElement => {
        return (
            <ContentContainer>
                <div className="no-details-message">
                    {mxStrings.noEmployeeDetails}
                </div>
            </ContentContainer>
        );
    };

    const getEmployeeInitials = (employeeDetails: Employee): string => {
        return extractInitials(
            employeeDetails.firstName,
            employeeDetails.lastName
        );
    };

    const getEmployeeName = (employeeDetails: Employee): string => {
        return buildFullName(
            employeeDetails.firstName,
            employeeDetails.lastName
        );
    };

    const renderEEInitials = (employeeDetails: Employee): React.ReactElement => {
        const initials = getEmployeeInitials(employeeDetails);
        return (
            <div className="ee-initials">
                <CircleDisplay
                    heading={initials}
                    large={true}
                />
            </div>
        );
    };

    const onJobTitleClick = (jobCode: string, jobId: string | number) => {
        navigateToNoMst.navigateToJobDetails(jobCode, jobId);
    };

    const renderEEName = (employeeDetails: Employee): React.ReactElement => {
        return (
            <div className="employee-name">
                <h2>
                    <span>{getEmployeeName(employeeDetails)}</span>
                </h2>
                <button
                    className="job-title pxl-link"
                    onClick={() => onJobTitleClick(employeeDetails.jobCode, employeeDetails.jobId || '')}
                    type="button"
                >
                    {employeeDetails.jobTitleInternal}
                </button>
            </div>
        );
    };

    const renderEENameHeader = (employeeDetails: Employee): React.ReactElement => {
        return (
            <div className="employee-header">
                <ContentContainer>
                    {renderEEInitials(employeeDetails)}
                    {renderEEName(employeeDetails)}
                </ContentContainer>
            </div>
        );
    };

    const renderCompensationSection = (employeeDetails: Employee): React.ReactElement => {
        const {
            isHourly,
            isoCurrencyCode,
            range,
            payEffectiveDate,
            totalCombinedCompensation} = employeeDetails;

        const payValue =  isHourly ? employeeDetails.convertedCurrentPay : employeeDetails.convertedAnnualizedCurrentPay;
        return (
            <DetailsSection className="compensation-section">
                {<CompensationCard
                    pay={payValue}
                    hourly={isHourly}
                    currency={isoCurrencyCode}
                    payEffectiveDate={payEffectiveDate?.toString()}
                    currencyFormatter={employeeList.formatCurrencyValue.bind(employeeList)}
                    dateFormatter={(date) => formatDate(date)}

                />}
                {employeeList.shouldShowRange && <RangePenetrationCard
                    pay={payValue}
                    isHourlyPay={isHourly}
                    currency={isoCurrencyCode}
                    rangeMin={range?.minRange || null}
                    rangeMax={range?.maxRange || null}
                    isHourlyRange={isHourly}
                    currencyFormatter={employeeList.formatCurrencyValue.bind(employeeList)}
                />}
                {employeeList.shouldShowTcc && <TotalCashCompensationCard
                    currency={isoCurrencyCode} 
                    totalCashCompensation={totalCombinedCompensation}
                    currencyFormatter={employeeList.formatCurrencyValue.bind(employeeList)}
                    hideBenefitsChart={true}
                />}
            </DetailsSection>
        );
    };

    const renderAnalyticsSection = (employeeDetails: Employee): React.ReactNode => {
        const {
            isHourly,
            performanceRating,
            range,
            firstName,
            rangeDistribution
        } = employeeDetails;
        const payValue =  isHourly ? employeeDetails.convertedCurrentPay : employeeDetails.convertedAnnualizedCurrentPay;
        if (!performanceRating || !range) {
            return null;
        }

        return (
            <DetailsSection className="analytics-section" title={mxStrings.analytics}>
                <PerformanceRatingCard
                    firstName={firstName}
                    performanceRating={performanceRating}
                />
                <RangeDistributionCard
                    employeeName={firstName}
                    rangeDistribution={rangeDistribution ?? []}
                    rangeMin={range.minRange}
                    rangeMax={range.maxRange}
                    pay={payValue}
                    isHourlyPay={isHourly}
                />
            </DetailsSection>
        );
    };

    const renderDetailsSection = (employeeDetails: Employee): React.ReactElement => {
        return (
            <DetailsSection
                className="employee-details-section"
                title={mxStrings.eeDetails}
            >
                <EmployeeDetailsCard
                    dateOfHire={employeeDetails.hireDate}
                    employeeId={employeeDetails.employeeSourceId}
                    workLocation={employeeDetails.workLocation}
                    managerName={employeeDetails.employeeInfo?.managerDisplayName}
                    email={employeeDetails.employeeInfo?.workEmail}
                    department={employeeDetails.department}
                    dateFormatter={(date) => formatDate(date)}
                />
            </DetailsSection>
        );
    };

    const renderDetailsContent = (employeeDetails: Employee): React.ReactElement => {
        // whether or not we want to show 'loading' view
        const placeholder = !employeeList.employeeDetailsFetched;
        const className = buildClassString({
            'employee-details-content': true,
            'placeholder': placeholder
        });

        return (
            <div className={className}>
                {renderEENameHeader(employeeDetails)}
                <ContentContainer nopadding>
                    {renderCompensationSection(employeeDetails) }
                    {!placeholder && renderAnalyticsSection(employeeDetails) }
                    {!placeholder && renderDetailsSection(employeeDetails) }
                </ContentContainer>
            </div>
        );
    };

    return (
        <div className="employee-details-view">
            {noDetails && renderNoDetailsMessage()}
            {!noDetails && selectedEmployee && renderDetailsContent(selectedEmployee)}
        </div>
    );
};

export default observer(EmployeeDetailsView);